import React, { FC, ReactElement, useEffect, useState } from 'react'

import FavoriteFoodOverviewCard from './FavoriteFoodOverviewCard'
import FavoriteMotionCardContainer from './FavoriteMotionCardContainer'
import FavoriteRecipeOverviewCard from './FavoriteRecipeOverviewCard'
import { FavoritesFilteredSectionList } from './FavoritesFilteredSectionList'
import { FavoriteTabNames, itemTypeNames } from './helpers/FavoriteTabNames'
import {
  getFavoriteEatableName,
  getFavoriteEatablesSectionList,
  getFavoriteMotionName,
  getFavoriteMotionsSectionList
} from './helpers/getFavoriteSectionList'

import { BackToTopButton, PageLayout, SearchBar } from 'Components'
import SidebarUserFood from 'Components/SidebarUserFood/SidebarUserFood'
import TabSwitcher from 'Components/TabSwitcher/TabSwitcher'
import {
  FavoritesFood,
  FavoritesMapping,
  FavoritesMotionLevel,
  FavoritesRecipe,
  QuantifiedFood,
  QuantifiedRecipe
} from 'Models'
import FoodModal from 'Pages/JournalPage/components/Modals/FoodModal/FoodModal'
import RecipeModal from 'Pages/JournalPage/components/Modals/RecipeModal/RecipeModal'
import { currentWeightSelector, fetchLatestBodyMeasurementsAction } from 'ReduxStore/bodyMeasurements'
import {
  favoriteSystemEatablesSelector,
  favoriteUserEatablesSelector,
  favoritesSelector
} from 'ReduxStore/favorites/favorites'
import { useAppDispatch, useAppSelector } from 'ReduxStore/hooks'

const FavoritesOverviewPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const currentWeight = useAppSelector(currentWeightSelector)
  const [detailFood, setDetailFood] = useState<QuantifiedFood>()
  const [detailRecipe, setDetailRecipe] = useState<QuantifiedRecipe>()

  const [currentTabName, setCurrentTabName] = useState(FavoriteTabNames.FOODS)
  const [searchQuery, setSearchQuery] = useState('')

  const favoritesMotionLevels = useAppSelector(favoritesSelector<FavoritesMotionLevel>(FavoritesMapping.MOTIONS))
  const favoriteUserEatables = useAppSelector(favoriteUserEatablesSelector)

  const favoriteSystemFoods = useAppSelector(favoriteSystemEatablesSelector<FavoritesFood>(FavoritesMapping.FOODS))
  const favoriteSystemRecipes = useAppSelector(
    favoriteSystemEatablesSelector<FavoritesRecipe>(FavoritesMapping.RECIPES)
  )

  useEffect(() => {
    if (!currentWeight) {
      dispatch(fetchLatestBodyMeasurementsAction({ type: 'WEIGHT' }))
    }
  }, [])

  const Eatables: FC = () => {
    const renderCard = (favorite: FavoritesFood | FavoritesRecipe): ReactElement =>
      'food' in favorite ? (
        <FavoriteFoodOverviewCard
          key={favorite.id}
          favorite={favorite}
          onClick={() => setDetailFood({ food: favorite.food, quantity: favorite.quantity, unit: favorite.unit })}
        />
      ) : (
        <FavoriteRecipeOverviewCard
          key={favorite.id}
          favorite={favorite}
          recipe={favorite.recipe}
          onClick={() => setDetailRecipe({ recipe: favorite.recipe, quantity: favorite.quantity, unit: favorite.unit })}
        />
      )

    return (
      <FavoritesFilteredSectionList
        sectionList={getFavoriteEatablesSectionList({
          key: currentTabName,
          favoriteSystemFoods,
          favoriteSystemRecipes,
          favoriteUserEatables
        })}
        searchQuery={searchQuery}
        getName={getFavoriteEatableName}
        itemTypeName={itemTypeNames[currentTabName]}
        renderCard={renderCard}
      />
    )
  }

  const Motions: FC = () => (
    <FavoritesFilteredSectionList
      sectionList={getFavoriteMotionsSectionList(favoritesMotionLevels)}
      searchQuery={searchQuery}
      getName={getFavoriteMotionName}
      itemTypeName={itemTypeNames[currentTabName]}
      renderCard={(favorite) => <FavoriteMotionCardContainer favorite={favorite} weight={currentWeight} />}
    />
  )

  return (
    <PageLayout variant="smallcircle">
      <div className="container max-900">
        <div className="row">
          <SidebarUserFood />

          <div className="col-12 col-lg-8">
            <h1>Favoriten</h1>

            <p>
              Hier finden Sie alle Ihre Favoriten. Sie können Menge und Einheit ändern und so die für Sie ideale Einheit
              speichern.
            </p>

            <div className="mt-3 mb-2">
              <SearchBar onChangeText={setSearchQuery} labelText="In Favoriten suchen …" />
            </div>

            <TabSwitcher
              onTabSwitch={(tabName) => {
                setCurrentTabName(tabName as FavoriteTabNames)
              }}
              tabs={[
                {
                  name: FavoriteTabNames.FOODS,
                  title: 'Nahrungsmittel',
                  renderContent: <Eatables />
                },
                {
                  name: FavoriteTabNames.RECIPES,
                  title: 'Rezepte',
                  renderContent: <Eatables />
                },
                {
                  name: FavoriteTabNames.USER_EATABLES,
                  title: 'Mein Essen',
                  renderContent: <Eatables />
                },
                {
                  name: FavoriteTabNames.MOTIONS,
                  title: 'Aktivitäten',
                  renderContent: <Motions />
                }
              ]}
            />
          </div>
        </div>
      </div>

      <BackToTopButton />

      <FoodModal quantifiedFood={detailFood} onClose={() => setDetailFood(undefined)} />

      <RecipeModal quantifiedRecipe={detailRecipe} onClose={() => setDetailRecipe(undefined)} />
    </PageLayout>
  )
}

export default FavoritesOverviewPage
